import { Helmet } from "react-helmet-async";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import posthog from "posthog-js";
import { IGuardProps } from "@interfaces";
import { useAuth, useGetUserDetail } from "@services";
import { InActiveUserModal, Loading } from "@components";
import { useRecoilValue } from "recoil";
import { userDetailsStore } from "@store/userDetail";
import { usePermissions, handleNavigation } from "@hooks/usePermissions";
import { ErrorPage } from "@pages";
import ChooseBundle from "../../pages/Subscribe/SubscribtionBundle/ChoosePlan";
import { trackRuntimeError } from "../../utils/posthogHelper";

/**
 * used for private pages to protect and add helmet
 */
const AuthGuard: FC<IGuardProps> = ({
  children,
  titleKey,
  allowInactive = false,
  requiredPermission,
}) => {
  const userDetailsState = useRecoilValue(userDetailsStore);
  const { data } = useGetUserDetail();
  const [loading, setLoading] = useState(true);
  const subscriptionRemainingDays =
    data?.result?.popUpMessage?.subscriptionRemainingDays ?? 0;

  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const { showInactiveModal } = useAuth();
  const permissions: any = usePermissions();
  // useEffect(() => {
  //   if (errorMessage) {
  //     Sentry.captureException(errorMessage);
  //   }
  // }, [errorMessage]);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 1000);
    const interval = setInterval(() => {
      const expiry = localStorage.getItem("exp");
      const expired = dayjs(Number(expiry)).isBefore(dayjs());
      if (expired) {
        localStorage.clear();
        navigate("/login");
      }
    }, 3000);

    return () => {
      clearInterval(interval);
    };
  }, []);
  if (!localStorage.token) {
    return <Navigate to={"/login"} replace state={{ pathname }} />;
  }
  const showSubscriptionModal = showInactiveModal() && !allowInactive;
  const mode = import.meta.env.VITE_MODE;
  const showError = mode == "STAGE" || mode == "DEV";

  // useEffect(() => {
  // 	if (userDetailsState?.result&&!userDetailsState?.result?.hasActiveSubscription) {
  // 	if (!userDetailsState?.result?.hasActiveSubscription) {
  // 		navigate("/manage-plan")
  // 	}
  // }, [userDetailsState, navigate, pathname])

  if (!loading && requiredPermission && !permissions[requiredPermission]) {
    const fallbackRoute = handleNavigation();
    return <ErrorPage code={403} />;
  }

  return (
    <>
      <Helmet key={titleKey}>
        <meta charSet="utf-8" />
        <meta name="title" content="Default Title" data-react-helmet="true" />
        {titleKey && <title>{t(titleKey)}</title>}
      </Helmet>
      <ErrorBoundary
        onError={(e) => {
          setErrorMessage(e?.message);
          console.log("🔥 error-catched 🔥 ", e);
          
          // Track runtime error to PostHog
          trackRuntimeError(posthog, e, {
            component: 'AuthGuard',
            page_title: titleKey,
            current_path: pathname,
          });
        }}
        fallback={
          <div
            className={
              "flex-1 bg-white m-8 p-4 flex flex-col items-center justify-center rounded-xl font-bold text-primary gap-4"
            }
          >
            <div>{showError ? errorMessage : t("ERRORS.SOMETHING_WENT_WRONG")}</div>
            {/* Temporary: Show error code for debugging - REMOVE AFTER FIXING */}
            <div className="text-xs text-gray-400 max-w-md text-center break-all">
              Error Code: {errorMessage?.substring(0, 100) || 'Unknown'}
            </div>
          </div>
        }
      >
        {subscriptionRemainingDays <= 0 &&
        data?.result?.popUpMessage?.isDisplay &&
        pathname !== "/chooseChannel" ? (
          <ChooseBundle />
        ) : (
          children
        )}
      </ErrorBoundary>
    </>
  );
};
export default AuthGuard;
